import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import useIndivdiualAkiyaHook from "../individualKominkaAkiya/useIndividualAkiyaHook";
import AkiyaGallery from "./AkiyaGallery";
import Heart from "../Svgs/Heart";
import MapIComponent from "./MapIndividual";
import Loader from "../Loader/Loader";
import { FaLinkedin, FaWhatsapp, FaFacebook, FaShareAlt, FaCopy } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";


// Utility functions for Kominka properties
const cleanAreaValue = (value) => {
  if (!value) return "N/A";
  const numberMatch = value.match(/[\d,.]+/);
  if (!numberMatch) return value;
  const number = parseFloat(numberMatch[0].replace(/,/g, ""));
  return isNaN(number) ? value : `${number} m²`;
};

const formatKominkaField = (field, value) => {
  // Special formatting for Kominka-specific fields
  const fieldLower = field.toLowerCase();
  
  if (fieldLower.includes("area") || fieldLower.includes("size")) {
    return cleanAreaValue(value);
  }
  
  if (fieldLower.includes("price") && value) {
    // Format price if numeric
    const numericPrice = parseFloat(value.toString().replace(/[^\d.-]/g, ''));
    if (!isNaN(numericPrice)) {
      return `¥${numericPrice.toLocaleString()}`;
    }
  }
  
  return value;
};

const IndividualKominkaAkiya = memo(() => {
  const {
    individualAkiya,
    setIndividualAkiya,
    tableData,
    handleToggleFavorite,
    isFavorite,
  } = useIndivdiualAkiyaHook();

  const [currentUrl, setCurrentUrl] = useState("");
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Get current URL for sharing
  useEffect(() => {
    if (typeof window !== "undefined") {
      setCurrentUrl(window.location.href);
    }
  }, []);

  // Kominka orange theme colors
  const themeColors = useMemo(() => ({
    primary: "#e09321",
    hover: "#c7811d",
    light: "#e0932120",
    shadow: "#e09321"
  }), []);

  // Enhanced sharing links for Kominka
  const shareLinks = useMemo(() => [
    {
      name: "LinkedIn",
      link: `https://www.linkedin.com/shareArticle?url=${encodeURIComponent(currentUrl)}&title=${encodeURIComponent('Amazing Traditional Japanese House - Kominka')}`,
      icon: <FaLinkedin className="w-5 h-5" />,
      color: "#0077B5"
    },
    {
      name: "Twitter",
      link: `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent('Check out this beautiful traditional Japanese house!')}`,
      icon: <FaXTwitter className="w-5 h-5" />,
      color: "#000000"
    },
    {
      name: "WhatsApp",
      link: `https://wa.me/?text=${encodeURIComponent(`Check out this traditional Kominka: ${currentUrl}`)}`,
      icon: <FaWhatsapp className="w-5 h-5" />,
      color: "#25D366"
    },
    {
      name: "Facebook",
      link: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
      icon: <FaFacebook className="w-5 h-5" />,
      color: "#1877F2"
    }
  ], [currentUrl]);

  // Copy to clipboard function
  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  }, [currentUrl]);

  // Extract key stats from table data
  const keyStats = useMemo(() => {
    if (!tableData) return [];
    
    const stats = [];
    
    Object.entries(tableData).forEach(([key, value]) => {
      const keyLower = key.toLowerCase();
      
      if (keyLower.includes('price') && value && value !== 'N/A') {
        stats.push({ label: 'Price', value: formatKominkaField(key, value) });
      } else if (keyLower.includes('land') && keyLower.includes('area')) {
        stats.push({ label: 'Land Area', value: formatKominkaField(key, value) });
      } else if (keyLower.includes('total') && keyLower.includes('area')) {
        stats.push({ label: 'House Area', value: formatKominkaField(key, value) });
      } else if (keyLower.includes('built') || keyLower.includes('year')) {
        stats.push({ label: 'Built', value });
      }
    });
    
    return stats.slice(0, 4); // Limit to 4 key stats
  }, [tableData]);

  if (!individualAkiya && !tableData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader big={true} />
          <p className="mt-4 text-gray-600">Loading traditional house details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white">
      {/* Background decorative elements with orange theme */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-64 h-64 bg-orange-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-32 w-64 h-64 bg-orange-300/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative flex justify-center">
        <div className="max-w-7xl w-full mx-4 lg:mx-8 py-8">
          
          {/* Enhanced Gallery Section */}
          <section className="mb-12">
            {individualAkiya?.images ? (
              <AkiyaGallery images={individualAkiya.images} />
            ) : (
              <div className="h-96 bg-orange-100 rounded-xl flex items-center justify-center">
                <div className="text-center">
                  <Loader big={false} />
                  <p className="mt-4 text-orange-600">Loading gallery...</p>
                </div>
              </div>
            )}
          </section>

          {/* Enhanced Header Section */}
          <section className="mb-8">
            <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-orange-200/50 p-6 lg:p-8">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                
                {/* Property Information */}
                <div className="flex-1 space-y-4">
                  <div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
                      Traditional Japanese House
                    </h1>
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      <p className="text-lg text-gray-600">
                        Kominka Smile Listing
                      </p>
                      
                      {/* Kominka Badge */}
                      <span 
                        className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold text-white shadow-md"
                        style={{ backgroundColor: themeColors.primary }}
                      >
                        🏮 Traditional Kominka
                      </span>
                    </div>
                  </div>

                  {/* Key Stats for Kominka */}
                  {keyStats.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4">
                      {keyStats.map((stat, index) => (
                        <div key={index} className="text-center p-3 bg-orange-50 rounded-lg">
                          <div className="text-lg font-bold text-gray-900">{stat.value}</div>
                          <div className="text-sm text-gray-600">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-row lg:flex-col items-center lg:items-end gap-4">
                  
                  {/* Favorite Button with Kominka orange theme */}
                  <button
                    onClick={handleToggleFavorite}
                    className={`group flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                      isFavorite
                        ? 'text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    style={isFavorite ? { backgroundColor: themeColors.primary } : {}}
                  >
                    <div className="w-6 h-6">
                      <Heart />
                    </div>
                    <span className="hidden sm:inline">
                      {isFavorite ? "Favorited" : "Add to Favorites"}
                    </span>
                  </button>

                  {/* Share Button */}
                  <div className="relative">
                    <button
                      onClick={() => setShowShareMenu(!showShareMenu)}
                      className="flex items-center gap-3 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105"
                    >
                      <FaShareAlt className="w-5 h-5" />
                      <span className="hidden sm:inline">Share</span>
                    </button>

                    {/* Enhanced Share Dropdown */}
                    {showShareMenu && (
                      <>
                        {/* Backdrop */}
                        <div 
                          className="fixed inset-0 z-10" 
                          onClick={() => setShowShareMenu(false)}
                        ></div>
                        
                        {/* Share Menu */}
                        <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-6 z-20">
                          <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Share this Kominka</h3>
                            
                            {/* Share Links Grid */}
                            <div className="grid grid-cols-2 gap-3">
                              {shareLinks.map((link) => (
                                <a
                                  key={link.name}
                                  href={link.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 group"
                                >
                                  <div style={{ color: link.color }}>
                                    {link.icon}
                                  </div>
                                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{link.name}</span>
                                </a>
                              ))}
                            </div>
                            
                            {/* Copy Link Section */}
                            <div className="pt-4 border-t border-gray-100">
                              <button
                                onClick={copyToClipboard}
                                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200 ${
                                  copySuccess 
                                    ? 'bg-green-50 text-green-700' 
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                }`}
                              >
                                <FaCopy className="w-5 h-5" />
                                <span className="text-sm font-medium">
                                  {copySuccess ? "Link copied!" : "Copy link"}
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Enhanced Map Section */}
          {individualAkiya?.Coordinate && (
            <section className="mb-12">
              <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-orange-200/50 overflow-hidden">
                <div className="p-6 border-b border-orange-200/50">
                  <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-3">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Traditional House Location
                  </h2>
                </div>
                <div className="h-96">
                  <MapIComponent
                    apiKey="AIzaSyDhd_bCluPIx2cSHS7pGd5wVPkIRU7rpxQ"
                    coordinates={individualAkiya.Coordinate}
                  />
                </div>
              </div>
            </section>
          )}

          {/* Enhanced Property Details Table */}
          <section className="mb-12">
            <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-orange-200/50 overflow-hidden">
              <div 
                className="px-6 py-4 text-white text-xl font-semibold flex items-center gap-3"
                style={{ backgroundColor: themeColors.primary }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Traditional House Details
              </div>
              
              {tableData && (
                <div className="divide-y divide-orange-100">
                  {Object.entries(tableData).map(([field, value], index) => {
                    const displayValue = formatKominkaField(field, value);

                    return (
                      <div
                        key={field}
                        className="flex flex-col sm:flex-row gap-4 p-4 sm:p-6 hover:bg-orange-50/50 transition-colors duration-200"
                      >
                        <div className="sm:w-1/3 font-semibold text-gray-700 flex items-center">
                          {field}
                        </div>
                        <div className="sm:w-2/3 text-gray-900">
                          {field === "Link to Official page" && value !== "N/A" ? (
                            <button
                              onClick={() => window.open(value, "_blank")}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-md"
                              style={{ backgroundColor: themeColors.primary }}
                            >
                              <span>Visit Official Site</span>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </button>
                          ) : (
                            <p className="text-gray-900 leading-relaxed">{displayValue}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* Kominka Cultural Information Section */}
          <section className="mb-12">
            <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-orange-200/50 p-6 lg:p-8">
              <div className="text-center space-y-4">
                <h2 className="text-2xl font-semibold text-gray-900 flex items-center justify-center gap-3">
                  <span className="text-2xl">🏮</span>
                  About Traditional Japanese Houses (Kominka)
                </h2>
                <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  Kominka are traditional Japanese houses, typically featuring wooden construction, tatami floors, 
                  sliding doors (fusuma), and beautiful gardens. These historic properties offer a unique opportunity 
                  to experience authentic Japanese culture and architecture while providing a peaceful retreat from modern life.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <div className="text-2xl mb-2">🌸</div>
                    <div className="text-sm font-medium text-gray-700">Traditional Design</div>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <div className="text-2xl mb-2">🎋</div>
                    <div className="text-sm font-medium text-gray-700">Natural Materials</div>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <div className="text-2xl mb-2">🍃</div>
                    <div className="text-sm font-medium text-gray-700">Garden Views</div>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <div className="text-2xl mb-2">⛩️</div>
                    <div className="text-sm font-medium text-gray-700">Cultural Heritage</div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
});

IndividualKominkaAkiya.displayName = 'IndividualKominkaAkiya';

export default IndividualKominkaAkiya;